/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useParams } from 'react-router-dom';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
// local imports
import { GetTenderAttachmentResponse } from '~src/services/api';
import { TableEntry } from './FileTable.utils';
import {
  DbFocusOptions,
  DbTenderFolder,
  DbShareTenderFolder,
} from '~src/types/db';

import { useSharedTenderFolders } from '~src/hooks/api-hooks/useSharedTenderFolders';
import { useSharedTenderSubfolders } from '~src/hooks/api-hooks/useSharedTenderFoldersFolders';
import { StructuredFoldersTendersDocumentsTable } from './StructuredFoldersTendersDocuemntsTable';
import { useSharedTenderFolderFiles } from '~src/hooks/api-hooks/useSharedFolderFiles';
import { useSharedTendersTenderAttachments } from '~src/hooks/api-hooks/useSharedTendersAtachments';
import { QueryResult } from 'react-query';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';

export interface MyTendersDocumentsTableProps extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  onFoldersListChange?: (
    folders: (DbTenderFolder | DbShareTenderFolder)[]
  ) => void;
  handleRow: Required<MyTendersDocumentsTableProps>['onRow'];
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
  parentContainerHeight?: string;
  showDeleted?: boolean;
  shareKey?: string;
  shareId?: string;
  tableColumnsConfig?: {
    onGetTenderAttachment?: (params: {
      attachmentId?: string;
    }) => Promise<GetTenderAttachmentResponse>;
    onGetTenderVersionedFile?: (params: {
      attachmentId?: string;
      versionId?: string;
      currentFolderId?: string;
      rootFolderId?: string;
    }) => Promise<GetTenderAttachmentResponse>;
  };
}

/** Default MyTendersDocumentsTable, used with folder navigation */
export const MyTendersDocumentsTable: React.FC<MyTendersDocumentsTableProps> = ({
  shareId,
  shareKey,
  highlightedRow,
  ...props
}) => {
  const { tenderId, currentFolderId, rootFolderId } = useTenderParamsFromUrl({
    isOnShare: true,
  });

  const isFirstLevel = !currentFolderId;

  const foldersQueryResult = useSharedTenderFolders(
    {
      id: shareId,
      key: shareKey,
    },
    {
      enabled: !!shareKey && isFirstLevel,
      refetchOnMount: false,
    }
  );

  const subfoldersQueryResult = useSharedTenderSubfolders(
    { root_folder_id: rootFolderId, share_key: shareKey, share_id: shareId },
    { enabled: !!shareKey && !isFirstLevel, refetchOnMount: false }
  );

  const foldersQuery: QueryResult<DbTenderFolder[]> = isFirstLevel
    ? foldersQueryResult
    : subfoldersQueryResult;

  useEffect(() => {
    props.onFoldersListChange?.((foldersQuery.data || []) as any);
  }, [foldersQuery.data]);

  const tenderFolderVersionedFilesQueryResult = useSharedTenderFolderFiles(
    {
      share_id: shareId,
      folder_id: currentFolderId,
      share_key: shareKey,
      root_folder_id: rootFolderId,
    },
    { enabled: !!shareKey && !isFirstLevel }
  );

  const attachmentsTenderQuery = useSharedTendersTenderAttachments(
    {
      share_id: shareId,
      share_key: shareKey,
    },
    { enabled: !!shareKey && isFirstLevel }
  );

  const attachmentsQuery = isFirstLevel
    ? attachmentsTenderQuery
    : tenderFolderVersionedFilesQueryResult;

  return (
    <StructuredFoldersTendersDocumentsTable
      foldersList={foldersQuery.data}
      attachmentsList={attachmentsQuery.data}
      shareId={shareId}
      shareKey={shareKey}
      isOnMyDocumentsTable={true}
      isListLoading={attachmentsQuery.isLoading || foldersQuery.isLoading}
      highlightedRow={highlightedRow}
      {...props}
    />
  );
};
